import { gql } from '@apollo/client'

import { CreateApiKeyInput } from '../gql/graphql'
import { apolloClient } from './apolloClient'

export const createApiKeyMutation = gql`
  mutation CreateApiKey($input: CreateApiKeyInput!) {
    createApiKey(input: $input) {
      id
      name
      createdDate
      updatedDate
      lastUsedAt
      expiresAt
      isActive
      apiKey
    }
  }
`

export const revokeApiKeyMutation = gql`
  mutation RevokeApiKey($id: String!) {
    revokeApiKey(id: $id) {
      id
    }
  }
`

export const apiKeysQuery = gql`
  query ApiKeys($first: Int, $skip: Int, $orderBy: [ApiKeyOrderByInput!]) {
    ApiKey_list(first: $first, skip: $skip, orderBy: $orderBy) {
      items {
        id
        name
        createdDate
        updatedDate
        lastUsedAt
        expiresAt
        isActive
      }
    }
  }
`

export interface ApiKey {
  id: string
  name: string
  createdDate: string
  updatedDate: string
  lastUsedAt?: string
  expiresAt?: string
  isActive: boolean
}

export interface CreateApiKeyRequest {
  name: string
  expiresAt?: string
}

export interface CreateApiKeyResponse extends ApiKey {
  apiKey: string // Only returned once during creation
}

/**
 * Creates a new API key. The key will only be shown once upon creation.
 */
export const createApiKey = async (input: CreateApiKeyInput) => {
  try {
    const result = await apolloClient.mutate({
      mutation: createApiKeyMutation,
      variables: {
        input,
      },
    })
    return (result.data as any)?.createApiKey!
  } catch (e: unknown) {
    console.error('Error creating API key:', e)
    throw e
  }
}

/**
 * Lists all API keys for the user (excluding the key values)
 */
export const listApiKeys = async (): Promise<ApiKey[]> => {
  try {
    const result = await apolloClient.query({
      query: apiKeysQuery,
      variables: {
        first: 200,
        skip: 0,
        orderBy: [{ createdDate: 'DESC' }],
      },
    })
    return ((result.data as any)?.ApiKey_list?.items ?? []) as ApiKey[]
  } catch (e: unknown) {
    console.error('Error listing API keys:', e)
    throw e
  }
}

/**
 * Revokes an API key by ID
 */
export const revokeApiKey = async (id: string) => {
  try {
    const result = await apolloClient.mutate({
      mutation: revokeApiKeyMutation,
      variables: {
        id,
      },
    })
    return (result.data as any)?.revokeApiKey
  } catch (e: unknown) {
    console.error('Error revoking API key:', e)
    throw e
  }
}
